import styles from './GranularityNavigation.module.scss'
import { Granularity } from 'types/contracts.ts'
import { GranularityNavigationInputs, GranularityNavigationProps, NavigationItemType } from './types.ts'
import { getCycleIndex, getGranularityPeriodIndex } from './utils.ts'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Select } from '@boltenergy-be/design-system'
import useWindowSize from 'hooks/useWindowSize.tsx'
import AddressSwitcher from 'components/address-switcher/AdressSwitcher.tsx'
import { SMALL_DESKTOP_BREAKPOINT } from 'constants/viewport.ts'
import { GRANULARITY_DAYJS_END_OF, DATE_INPUT_TYPE } from './constants.ts'
import DateField from 'components/form-fields/date-field/DateField.tsx'
import { ISO8601_DATE_FORMAT, MONTH_PICKER_FORMAT } from 'constants/constants.ts'
import { toBelgianTime } from 'utils/date.ts'

const GranularityNavigation = ({
  allowSwitchAddress = true,
  alwaysShowGranularitySelector,
  billingCycles = {},
  children,
  disabledFilters,
  granularities,
  labelContext
}: GranularityNavigationProps) => {
  // i18n
  const { t } = useTranslation('consumption')

  // Window size
  const { isTablet, isSmaller } = useWindowSize(SMALL_DESKTOP_BREAKPOINT)

  // React hook form
  const { register, watch, setValue } = useFormContext<GranularityNavigationInputs>()
  const watchView = watch('view')
  const watchPeriod = watch('period')

  // Constants
  const viewOptionCount = Object.keys(granularities).length + (Object.keys(billingCycles).length ? 1 : 0)
  const viewSelectorVisibleButDisabled = alwaysShowGranularitySelector && viewOptionCount === 1

  /**
   * Handle the change of the view
   * @param {Granularity | NavigationItemType.CYCLE} newView
   */
  const handleChangeView = (newView: Granularity | NavigationItemType.CYCLE) => {
    setValue('view', newView)

    if (newView === NavigationItemType.CYCLE) {
      setValue('period', Object.keys(billingCycles).sort((a, b) => Number(b) - Number(a))?.[0])
    } else {
      const defaultPeriod = granularities[newView]?.periods?.find((period) => period.defaultSelected)
      const newPeriod = defaultPeriod ? defaultPeriod?.value : granularities[newView]?.periods?.[0]?.value
      setValue('period', newPeriod ?? '')
    }
  }

  return (
    <form className={styles.form}>
      {/* CONSUMPTION VIEW (granularity/billing cycles) */}
      {(viewSelectorVisibleButDisabled || viewOptionCount > 1) && (
        <Select
          disabled={disabledFilters || viewSelectorVisibleButDisabled}
          label={t(($) => $.yourConsumption.granularity)}
          hiddenLabel
          {...register('view')}
          layoutClassName={styles['granularity-layout']}
          className={styles.granularity}
          onChange={(event) => handleChangeView(event.target.value as Granularity | NavigationItemType.CYCLE)}
        >
          {billingCycles && !!Object.keys(billingCycles).length && (
            <option value={NavigationItemType.CYCLE}>{t(($) => $.chart.header.pagination.billingCycle)}</option>
          )}
          {Object.values(Granularity)
            .filter((g) => !!granularities[g])
            .map((g) => (
              <option key={g} value={g}>
                {granularities[g]!.translationKey
                  ? t(($) => $.chart.header.pagination[granularities[g]!.translationKey!], {
                      context: labelContext
                    })
                  : granularities[g]!.text}
              </option>
            ))}
        </Select>
      )}

      {/* SELECTED PERIOD NAVIGATION */}
      <div className={styles.navigation}>
        <Button
          className={styles.previous}
          type="button"
          variant={isTablet ? 'secondary' : 'layered'}
          size="small"
          leadingIcon="chevronLeft"
          disabled={
            disabledFilters ||
            (watchView === NavigationItemType.CYCLE
              ? getCycleIndex(billingCycles, watchPeriod).previous === null
              : getGranularityPeriodIndex(granularities[watchView]?.periods || [], watchPeriod).previous === null)
          }
          onClick={() => {
            if (watchView === NavigationItemType.CYCLE) {
              const previousCycleIndex = getCycleIndex(billingCycles, watchPeriod).previous
              if (typeof previousCycleIndex === 'number') {
                const previous = Object.values(billingCycles)[previousCycleIndex]
                setValue('period', previous.id)
              }
            } else {
              const previousGranularityIndex = getGranularityPeriodIndex(granularities[watchView]?.periods || [], watchPeriod).previous
              if (typeof previousGranularityIndex === 'number') {
                const previous = granularities[watchView]?.periods?.[previousGranularityIndex]
                setValue('period', previous?.value || '')
              }
            }
          }}
        />

        {watchView === NavigationItemType.CYCLE || watchView === Granularity.MONTH ? (
          <Select
            hideIcon
            disabled={disabledFilters}
            label={t(($) => $.yourConsumption.period)}
            hiddenLabel
            {...register('period')}
            className={styles.period}
            layoutClassName={styles['period-layout']}
            value={watchPeriod}
          >
            {watchView === NavigationItemType.CYCLE
              ? Object.keys(billingCycles)
                  .sort((a, b) => Number(b) - Number(a))
                  .map((key) => (
                    <option key={key} value={billingCycles[key].id}>
                      {billingCycles[key].text}
                    </option>
                  ))
              : granularities[watchView]?.periods?.map((period) => (
                  <option key={`${watchView}-${period.value}`} value={period.value}>
                    {period.label}
                  </option>
                ))}
          </Select>
        ) : (
          <DateField
            hookFormKey="period"
            label={t(($) => $.yourConsumption.period)}
            hiddenLabel
            type={DATE_INPUT_TYPE[watchView]}
            max={toBelgianTime(granularities[watchView]?.periods?.[0]?.value).format(
              DATE_INPUT_TYPE[watchView] === 'month' ? MONTH_PICKER_FORMAT : ISO8601_DATE_FORMAT
            )}
            min={toBelgianTime(granularities[watchView]?.periods?.[(granularities[watchView]?.periods?.length || 1) - 1]?.value).format(
              DATE_INPUT_TYPE[watchView] === 'month' ? MONTH_PICKER_FORMAT : ISO8601_DATE_FORMAT
            )}
            value={
              DATE_INPUT_TYPE[watchView] === 'month'
                ? toBelgianTime(watchPeriod).format(MONTH_PICKER_FORMAT)
                : toBelgianTime(watchPeriod).format(ISO8601_DATE_FORMAT)
            }
            layoutClassName={styles['datepicker-layout']}
            onChange={(e) => {
              const valueFormat = DATE_INPUT_TYPE[watchView] === 'month' ? MONTH_PICKER_FORMAT : ISO8601_DATE_FORMAT
              const endOf = GRANULARITY_DAYJS_END_OF[watchView]
              const date = e.target.valueAsDate

              // valueAsDate is UTC midnight of the picked date, which is the previous day on a device behind UTC
              const periodToSelect = granularities[watchView]?.periods?.find(
                (p) => toBelgianTime(p.value).format(valueFormat) === toBelgianTime(date).endOf(endOf).format(valueFormat)
              )?.value
              const defaultPeriod = granularities[watchView]?.periods?.[0]?.value

              setValue('period', periodToSelect || defaultPeriod || '')
            }}
          />
        )}

        <Button
          className={styles.next}
          type="button"
          variant={isTablet ? 'secondary' : 'layered'}
          size="small"
          leadingIcon="chevronRight"
          disabled={
            disabledFilters ||
            (watchView === NavigationItemType.CYCLE
              ? getCycleIndex(billingCycles, watchPeriod).next === null
              : getGranularityPeriodIndex(granularities[watchView]?.periods || [], watchPeriod).next === null)
          }
          onClick={() => {
            if (watchView === NavigationItemType.CYCLE) {
              const nextCycleIndex = getCycleIndex(billingCycles, watchPeriod).next
              if (typeof nextCycleIndex === 'number') {
                const next = Object.values(billingCycles)[nextCycleIndex]
                setValue('period', next.id)
              }
            } else {
              const nextGranularityIndex = getGranularityPeriodIndex(granularities[watchView]?.periods || [], watchPeriod).next
              if (typeof nextGranularityIndex === 'number') {
                const next = granularities[watchView]?.periods?.[nextGranularityIndex]
                setValue('period', next?.value || '')
              }
            }
          }}
        />
      </div>

      {allowSwitchAddress && !isSmaller && (
        <AddressSwitcher containerClassName={styles['switcher-container']} className={styles.switcher} />
      )}

      {!!children && children}
    </form>
  )
}

export default GranularityNavigation
